import { BUILDING_DEFS, MAP_H, MAP_W, colOf, inBounds } from '../constants';
import { FLAG_POWER, FLAG_RAIL, type BuildingType, type SimState } from '../types';

/** Columns of a building's reserved right-of-way: the row just south of the
 * footprint, running to the map edge the line comes in from — rail depots
 * west (the railroad), grid interconnects east (the territorial grid). */
export function corridorCols(type: BuildingType, x: number, y: number): number[] {
  const def = BUILDING_DEFS[type];
  if (!def.corridor) return [];
  const row = y + def.h;
  if (row >= MAP_H) return [];
  const cols: number[] = [];
  if (def.corridor === 'west') {
    for (let tx = 0; tx <= x + def.w - 1; tx += 1) {
      if (inBounds(tx, row)) cols.push(colOf(tx, row));
    }
  } else {
    for (let tx = x; tx < MAP_W; tx += 1) {
      if (inBounds(tx, row)) cols.push(colOf(tx, row));
    }
  }
  return cols;
}

/** Back-compat alias for the rail-only callers. */
export function railCorridorCols(x: number, y: number): number[] {
  return corridorCols('railDepot', x, y);
}

/**
 * Recompute every corridor flag bit (FLAG_RAIL from rail depots, FLAG_POWER
 * from grid interconnects) from the surviving buildings. Returns the columns
 * whose flags changed so callers can re-bake those terrain tiles. Idempotent —
 * also used to normalize saves from before corridors were reserved.
 */
export function rebuildRailFlags(state: SimState): number[] {
  const flags = state.terrain.flags;
  const wantedRail = new Set<number>();
  const wantedPower = new Set<number>();
  for (const building of state.buildings) {
    const corridor = BUILDING_DEFS[building.type].corridor;
    if (!corridor) continue;
    const target = corridor === 'west' ? wantedRail : wantedPower;
    for (const col of corridorCols(building.type, building.x, building.y)) target.add(col);
  }
  const changed: number[] = [];
  for (let col = 0; col < flags.length; col += 1) {
    const current = flags[col] ?? 0;
    let next = current;
    next = wantedRail.has(col) ? next | FLAG_RAIL : next & ~FLAG_RAIL;
    next = wantedPower.has(col) ? next | FLAG_POWER : next & ~FLAG_POWER;
    if (next === current) continue;
    flags[col] = next;
    changed.push(col);
  }
  if (changed.length > 0) state.terrain.version += 1;
  return changed;
}
